import { useMutation, useQuery } from "@apollo/client/react"; import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import CardBox from "../../CardBox/CardBox";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import ResultadoPH from "../../ResultadoPH";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_PROCEDIMIENTO_PRESENCIAL_AE, { adaptAddVisitaPresencialAEProcedimientoInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProcedimientoPresencialAE";
import SAVE_ALMACENAMIENTO_PRESENCIAL_AE, { adaptAddVisitaPresencialAEAlmacenamientoInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProcedimientoAlmacenamientoPresencialAE";
import { useStepSave } from "../../../../hooks/useStepSave";
import VISITA_PUEDE_CAMBIAR_SNG from "../../../../../../data/graphQL/queries/MisVisitas/visitaPuedeCambiarSNG";

import { validateStepFields } from "../../../../utils/visitaStepValidation";

export default function ProcedimientoPresencialAE({
  esUrgencia,
  readOnly,
  isClone,
  onSaveRef,
  onExposeFormatted,
  currentStackScreens,
  setCurrentStackScreens,
  refetchPantallas
}) {
  const [formData, setFormData] = useState({});
  const conditionalScreens = ['ConstanciaColocacionSonda', 'ProcedimientoPresencialAE2'];
  const { visita, setVisita } = useVisita();

  const [saveProcedimiento] = useMutation(SAVE_PROCEDIMIENTO_PRESENCIAL_AE);
  const [saveAlmacenamiento] = useMutation(SAVE_ALMACENAMIENTO_PRESENCIAL_AE);
  const [puedeCambiarSNG, setPuedeCambiarSNG] = useState(false);
  const { data: dataPuedeCambiarSNG } = useQuery(VISITA_PUEDE_CAMBIAR_SNG, {
    fetchPolicy: 'no-cache',
    variables: { idEvaluacion: visita?.idEvaluacion }
  });

  useEffect(() => {
    if (!readOnly) return;
    if (!!readOnly && !formData?.cambioSonda && !formData?.controlPH)
      setCurrentStackScreens((prevScreens) => prevScreens.filter((screen) => !conditionalScreens.includes(screen.componentName)));
    else if (conditionalScreens.every((screenName) => currentStackScreens.some((screen) => screen.componentName === screenName)))
      return;
    else
      refetchPantallas();
  }, [readOnly, formData?.cambioSonda, formData?.controlPH]);

  useEffect(() => {
    if (!dataPuedeCambiarSNG) return;
    setPuedeCambiarSNG(!!dataPuedeCambiarSNG?.visitaPuedeCambiarSNG);
  }, [dataPuedeCambiarSNG]);

  const handleChange = useCallback((name, value) => {
    if (name === "cambioSonda" && value === true) {
      setFormData((prev) => ({
        ...prev,
        cambioSonda: true,
        controlPH: true
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const formattedGroups = useMemo(() => {
    return groups.map((group) => {
      const formattedInputs = group.inputs.
        map((input) => {
          const newInput = { ...input };

          const tipoAcceso = formData.tipoAcceso;
          switch (input.name) {
            case "tensionArterial":
              newInput.required = visita?.edad > 15;
              break;
            case "cambioSonda":
              const showCambioSonda = (tipoAcceso === "SNG" || tipoAcceso === "SOG") && puedeCambiarSNG !== false;
              newInput.disabled = !showCambioSonda;
              newInput.required = !!showCambioSonda;
              break;
            case "controlPH":
              newInput.disabled = formData.cambioSonda || (tipoAcceso !== "SNG" && tipoAcceso !== "SOG");
              break;
            case "obsCondicionAlmacenamiento":
              newInput.hidden = formData?.condicionAlmacenamiento !== false;
              newInput.required = formData?.condicionAlmacenamiento === false;
              break;
            case "obsStockProductos":
              newInput.hidden = formData?.stockProductos !== false;
              newInput.required = formData?.stockProductos === false;
              break;
          }

          if (esUrgencia) newInput.required = false;

          if (readOnly) newInput.disabled = true;

          if (newInput.name === "cambioSonda" && !!isClone) newInput.disabled = true;

          return newInput;
        }).
        filter(Boolean);

      return { ...group, inputs: formattedInputs };
    });
  }, [
    readOnly,
    isClone,
    esUrgencia,
    formData.tipoAcceso,
    formData.cambioSngDomicilio,
    formData.controlPH,
    formData.cambioSonda,
    formData?.condicionAlmacenamiento,
    formData?.stockProductos,
    visita?.edad,
    puedeCambiarSNG
  ]
  );

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });

    const [procedimientoResult, almacenamientoResult] = await Promise.all([
      await saveProcedimiento({ variables: { input: adaptAddVisitaPresencialAEProcedimientoInput(formData) } }),
      await saveAlmacenamiento({ variables: { input: adaptAddVisitaPresencialAEAlmacenamientoInput(formData) } })]
    );

    const idPresencialAEProc = procedimientoResult?.data?.saveVisitaPresencialAEProcedimiento?.idPresencialAE;
    const idPresencialAEAlma = almacenamientoResult?.data?.saveVisitaPresencialAEAlmacenamiento?.idPresencialAE;
    if (!idPresencialAEProc || !idPresencialAEAlma) throw new Error("Error al guardar");

    setVisita((prev) => ({
      ...prev,
      ...formData
    }));

    if (!formData?.cambioSonda && !formData?.controlPH)
      setCurrentStackScreens((prevScreens) => prevScreens.filter((screen) => !conditionalScreens.includes(screen.componentName)));
    else if (conditionalScreens.every((screenName) => currentStackScreens.some((screen) => screen.componentName === screenName)))
      return;
    else
      refetchPantallas();
  });

  useEffect(() => {
    const showControlPH = (formData.tipoAcceso === "SNG" || formData.tipoAcceso === "SOG");
    const showCambioSonda = showControlPH && puedeCambiarSNG === true;
    setFormData((prevDatos) => ({
      ...prevDatos,
      cambioSonda: !showCambioSonda ? false : prevDatos.cambioSonda,
      controlPH: !showControlPH ? false : prevDatos.controlPH
    }));
  }, [formData.tipoAcceso, puedeCambiarSNG]);

  useEffect(() => {
    setFormData((prevDatos) => ({
      ...visita,
      'cambioSonda': visita?.cambioSonda ?? prevDatos?.cambioSonda,
    }));
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: 'procedimientoPresencialAE',
      stepName: 'Procedimiento',
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
      {formattedGroups.map((group, groupIndex) =>
        <CardBox key={groupIndex} title={group.title || undefined}>
          <Grid container spacing={2}>
            {group.inputs.
              filter((input) => !input.hidden).
              map((input) =>
                <Grid size={{ xs: input.halfSpace ? 6 : 12 }} key={input.name}>
                  {input.name === 'listResultadoPH' && <ResultadoPH
                    key={`${input.name}-${input.type}`}
                    value={formData[input.name]}
                    handleChangeList={handleChange}
                    readOnly={readOnly} />
                  }
                  {input.name !== 'listResultadoPH' && <RenderInputByTypeMobile
                    input={input}
                    key={`${input.name}-${input.type}${input.type === 'multiple_boolean' ? formData[input.name] : ''}`}
                    value={formData[input.name]}
                    handleChange={handleChange}
                    row={input.type === 'multiple_boolean'} />
                  }
                </Grid>
              )}
          </Grid>
        </CardBox>
      )}
    </>);

}

const groups = [
  {
    title: "Control de signos vitales",
    inputs: [
      {
        name: "tensionArterial",
        displayName: "Tensión arterial",
        type: "string",
        halfSpace: true
      },
      {
        name: "temperatura",
        displayName: "Temperatura",
        type: "float",
        required: true,
        halfSpace: true
      }, ,

      {
        name: "frecuenciaCardiaca",
        displayName: "Frecuencia Cardiaca",
        type: "float",
        halfSpace: true
      },
      {
        name: "frecuenciaRespiratoria",
        displayName: "Frecuencia Respiratoria",
        type: "float",
        halfSpace: true
      },
      {
        name: "fijacionAcceso",
        displayName: "Fijación de acceso",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "curacionAcceso",
        displayName: "Curación de acceso",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "bombaInfusion",
        displayName: "Control de bomba de infusión",
        type: "multiple_boolean",
        lightTitle: true,
        required: true,
        options: [
          { value: true, label: "Apto" },
          { value: false, label: "No Apto" }]

      },
      {
        name: "condicionAlmacenamiento",
        displayName: "Condición de almacenamiento",
        type: "multiple_boolean",
        lightTitle: true,
        required: true,
        options: [
          { value: true, label: "Óptimo" },
          { value: false, label: "No Óptimo" }]

      },
      {
        name: "obsCondicionAlmacenamiento",
        displayName: "Obs. No Óptimo",
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "stockProductos",
        displayName: "Stock de Productos",
        type: "multiple_boolean",
        lightTitle: true,
        required: true,
        options: [
          { value: true, label: "Óptimo" },
          { value: false, label: "No Óptimo" }]

      },
      {
        name: "obsStockProductos",
        displayName: "Obs. No Óptimo",
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "cambioSonda",
        displayName: "Cambio de sonda",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "controlPH",
        displayName: "Control de PH",
        type: "boolean",
        lightTitle: true
      }]

  }];